import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DATE_KEYS } from 'legacyfrontend/constants';
import { formatWithMomentFormat } from 'legacyfrontend/utils/dateUtils';
import { cityName, extractTime, formatStopoverWait } from './previewUtils';

function StopoverPath({ stops }) {
  const { t } = useTranslation('layout');
  const [tooltip, setTooltip] = useState(null);

  if (!stops?.length) {
    return t('packages.preview.directTrip');
  }

  const label = t('packages.preview.stopovers', { count: stops.length });

  function showTooltip(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const maxWidth = 240;
    const left = Math.min(
      rect.left + rect.width / 2 - maxWidth / 2,
      window.innerWidth - maxWidth - 12
    );
    const top = rect.top - 8;
    setTooltip({ top, left: Math.max(12, left) });
  }

  return (
    <>
      <span className='escala-trigger' onMouseEnter={showTooltip} onMouseLeave={() => setTooltip(null)}>
        {label}
      </span>
      {tooltip &&
        createPortal(
          <div
            className='pkg-escala-tooltip-portal'
            style={{
              display: 'block',
              top: tooltip.top,
              left: tooltip.left,
              transform: 'translateY(-100%)',
            }}
          >
            {stops.map((stop, index) => {
              const city = cityName(stop.city || stop.localidad) || t('packages.preview.stopover');
              const duration = formatStopoverWait(stop.waitingHours, stop.waitingMinutes);
              const title = stops.length > 1 ? `${index + 1}. ${city}` : city;
              return (
                <div className='pkg-escala-tooltip-item' key={`${city}-${index}`}>
                  <strong>{title}</strong>
                  {` — ${duration}`}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}

export function buildFlightGroups(trips, t) {
  return (trips || []).map((trip, index) => {
    const typeText = trip?.tripType?.description || '';
    const isReturn = /vuelta/i.test(typeText) && !/ida/i.test(typeText);
    const isOutbound = /ida/i.test(typeText);
    const transport = trip?.transportType?.description || t('packages.details.transport');
    let title = transport;
    if (isOutbound) title = `${transport} ${t('packages.preview.tripLegOut')}`;
    else if (isReturn) title = `${transport} ${t('packages.preview.tripLegBack')}`;
    return {
      key: trip.id || trip.clientId || `trip-${index}`,
      title,
      trips: [trip],
      kind: isReturn ? 'vuelta' : 'ida',
    };
  });
}

function formatTripDay(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatWithMomentFormat(date, DATE_KEYS.FORMAT.DAY_SHORT);
}

export default function FlightGroup({ title, trips, kind }) {
  const { t } = useTranslation('layout');
  if (!trips.length) return null;
  const trip = trips[0];
  const departureLabel = formatTripDay(trip.departureDate || trip.originDate);
  const arrivalLabel = formatTripDay(trip.arrivalDate || trip.destinationDate);
  // Always show departure and arrival dates (including same-day flights)
  const dateLabel =
    departureLabel && arrivalLabel
      ? `${departureLabel} – ${arrivalLabel}`
      : departureLabel || arrivalLabel;

  return (
    <div className='flight-group'>
      <div className='flight-group-header'>
        <span>{title}</span>
        {dateLabel ? <span className='flight-group-date'>{dateLabel}</span> : null}
      </div>
      {trips.map((trip) => (
        <div key={trip.id || trip.clientId} className={`flight-card ${kind === 'vuelta' ? 'vuelta' : 'ida'}`}>
          <div className='flight-card-body'>
            <div className='flight-airline'>
              {trip.company || trip.transportType?.description || t('packages.details.transport')}
            </div>
            <div className='flight-times-row'>
              <div className='flight-time'>
                {extractTime(trip.departureDate || trip.originDate) || '--:--'}
              </div>
              <div className='flight-path'>
                <StopoverPath stops={trip.stopDetails} />
                <div className='flight-path-line'>
                  <span className='flight-path-dot' />
                  <span className='flight-path-track' />
                  <span className='flight-path-dot' />
                </div>
              </div>
              <div className='flight-time'>
                {extractTime(trip.arrivalDate || trip.destinationDate) || '--:--'}
              </div>
            </div>
            <div className='flight-cities-row'>
              <div>
                <strong>{cityName(trip.originCity) || '—'}</strong>
              </div>
              <div className='right'>
                <strong>{cityName(trip.destinationCity) || '—'}</strong>
              </div>
            </div>
            {trip.travelCode ? (
              <div className='flight-code'>
                {t('packages.preview.flightCode', { code: trip.travelCode })}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
